import { z } from 'zod';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validation contract for the /start-free supplier onboarding form.
export const startFreeSchema = z.object({
  legalName: z.string().trim().min(1, 'Company name is required.').max(200, 'Too long.'),
  brandName: z.string().trim().min(1, 'Brand name is required.').max(200, 'Too long.'),
  contactName: z.string().trim().min(1, 'Your name is required.').max(200, 'Too long.'),
  email: z
    .string()
    .trim()
    .min(1, 'Email is required.')
    .max(320, 'Too long.')
    .regex(EMAIL_RE, 'Enter a valid email address.')
    // Reject plus addressing (e.g. user+tag@domain.com) in the local part.
    .refine((value) => !value.split('@')[0]?.includes('+'), {
      message: 'Plus addressing (e.g. name+tag@domain.com) is not allowed.',
    }),
  locationState: z.string().trim().min(1, 'Select a state.').max(10),
  // Accept a bare domain (users type `company.com`) and ADD `https://`, so the stored value
  // is always an absolute, secure URL. If a scheme is typed it MUST be https — reject http://
  // (or any other protocol) instead of silently upgrading it. Mirrors the account-platform
  // onboarding schema.
  website: z
    .string()
    .trim()
    .min(1, 'Website is required.')
    .max(2048, 'Too long.')
    .refine(
      (v) => !/^[a-z][a-z0-9+.-]*:\/\//i.test(v) || /^https:\/\//i.test(v),
      'Use https:// — http is not allowed.'
    )
    .transform((v) => (/^https:\/\//i.test(v) ? v : `https://${v}`))
    .refine((v) => /^https:\/\/[^\s./]+(\.[^\s./]+)+/i.test(v), 'Enter a valid website URL.'),
  opportunitiesDescription: z.string().trim().max(2000, 'Too long.'),
  // Honeypot — must stay empty; bots that fill it are dropped silently.
  honeypot: z.string(),
});

export type StartFreeFormValues = z.infer<typeof startFreeSchema>;
